// Leaving a team (RFC-1371 §7.2 D7, §7.3).
//
// ── The 409 IS the prompt ──────────────────────────────────────────────────
//
// There is no leave-preview endpoint, by design. The first POST sends
// `private_projects: null`. If the leaver has private projects in this team the
// server refuses with 409 `private_projects_disposition_required` and names
// them; that response is what this view renders as the question, and the second
// POST carries the answer.
//
// This is better than a preview call and worth stating so nobody "improves" it
// back: a preview would be read-then-write, and the project list could change
// in between — so the user would answer a question about one set of projects
// while the server acted on another. Here the refusal and the list come from
// the same transaction that will do the work.
//
// It also means the happy path is one click. A leaver with no private projects
// never sees the question at all, so this view must not hard-gate on it.
//
// The radio starts unselected. §7.3 gives the two NON-interactive departure
// paths opposite defaults precisely because nobody is there to answer; this is
// the one path with a human in session, so it asks rather than picking.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  TeamsRequestError,
  leaveNeedsDisposition,
  type PrivateProjectDisposition,
  type Team,
  type TeamProjectRef,
} from '../../api/teams'
import { useLeaveTeam } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import {
  BackButton,
  Card,
  DestructiveButton,
  ErrorText,
  SecondaryButton,
  WarningPanel,
} from './teamsChrome'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'

export default function LeaveTeamView({
  team,
  onDone,
  onCancel,
}: {
  team: Team
  onDone: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('settings')
  const leaveTeam = useLeaveTeam()
  // Populated only once the server has told us there is something to decide.
  const [atRisk, setAtRisk] = useState<TeamProjectRef[] | null>(null)
  const [choice, setChoice] = useState<PrivateProjectDisposition | null>(null)
  const [blocked, setBlocked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = (privateProjects: PrivateProjectDisposition | null) => {
    setError(null)
    leaveTeam.mutate(
      { teamId: team.id, privateProjects },
      {
        onSuccess: () => {
          trackInteraction('teams_team_left', { team_id: team.id, actor_role: team.myRole, result: 'success' })
          onDone()
        },
        onError: (err) => {
          const projects = leaveNeedsDisposition(err)
          if (projects !== null) {
            // Not a failure — the question. Render it and wait.
            setAtRisk(projects)
            return
          }
          trackInteraction('teams_team_left', { team_id: team.id, actor_role: team.myRole, result: resultOf(false) })
          if (!(err instanceof TeamsRequestError)) {
            setError(t('settingsModal.teams.leaveFailed'))
            return
          }
          if (err.code === 'last_member_cannot_leave') {
            setBlocked(t('settingsModal.teams.leaveBlockedLastMember'))
            return
          }
          // Only the Owner is pinned to a personal team; everyone they invited
          // leaves by the ordinary path and never reaches this branch.
          if (err.code === 'personal_team_owner_cannot_leave') {
            setBlocked(t('settingsModal.teams.leaveBlockedPersonal'))
            return
          }
          setError(err.message_ ?? t('settingsModal.teams.leaveFailed'))
        },
      },
    )
  }

  const awaitingChoice = atRisk !== null && atRisk.length > 0

  return (
    <div className="flex flex-col gap-4" data-testid="teams-leave-view">
      <BackButton
        testId="teams-leave-back"
        label={t('settingsModal.teams.back')}
        onClick={onCancel}
      />

      <h4 className="text-lg font-semibold text-white">
        {t('settingsModal.teams.leaveTitle', { name: team.name })}
      </h4>

      {blocked !== null ? (
        <WarningPanel testId="teams-leave-blocked">{blocked}</WarningPanel>
      ) : (
        <>
          {/* D7: the Owner does not abandon the team, it is handed over. */}
          {team.myRole === 'owner' && (
            <Card className="px-4 py-3" testId="teams-leave-succession">
              <p className="text-sm leading-6 text-white/85">
                {t('settingsModal.teams.leaveSuccessionNotice')}
              </p>
            </Card>
          )}

          {awaitingChoice ? (
            <div data-testid="teams-leave-private-choice">
              <WarningPanel testId="teams-leave-private-warning">
                {t('settingsModal.teams.leavePrivateWarning', { count: atRisk.length })}
              </WarningPanel>

              <ul
                className="mt-2 max-h-40 overflow-y-auto rounded-xl bg-white/[0.04] px-4 py-2.5"
                data-testid="teams-leave-private-list"
              >
                {atRisk.map((project) => (
                  <li
                    key={project.projectId}
                    className="truncate py-0.5 text-sm text-white/80"
                    data-testid={`teams-leave-private-project-${project.projectId}`}
                  >
                    {project.name}
                  </li>
                ))}
              </ul>

              <div
                role="radiogroup"
                aria-label={t('settingsModal.teams.leavePrivateQuestion')}
                className="mt-3 flex flex-col gap-2"
              >
                <ChoiceRow
                  testId="teams-leave-choice-publish"
                  checked={choice === 'publish'}
                  onSelect={() => setChoice('publish')}
                  title={t('settingsModal.teams.leavePublishTitle')}
                  description={t('settingsModal.teams.leavePublishDescription')}
                />
                <ChoiceRow
                  testId="teams-leave-choice-discard"
                  checked={choice === 'discard'}
                  onSelect={() => setChoice('discard')}
                  title={t('settingsModal.teams.leaveDiscardTitle')}
                  description={t('settingsModal.teams.leaveDiscardDescription')}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm leading-6 text-white/70" data-testid="teams-leave-intro">
              {t('settingsModal.teams.leaveIntro', { name: team.name })}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <SecondaryButton testId="teams-leave-cancel" onClick={onCancel}>
              {t('settingsModal.teams.cancel')}
            </SecondaryButton>
            <DestructiveButton
              testId="teams-leave-submit"
              disabled={leaveTeam.isPending || (awaitingChoice && choice === null)}
              onClick={() => submit(awaitingChoice ? choice : null)}
            >
              {t('settingsModal.teams.leaveTeam')}
            </DestructiveButton>
          </div>
        </>
      )}

      {error !== null && <ErrorText testId="teams-leave-error">{error}</ErrorText>}
    </div>
  )
}

function ChoiceRow({
  testId,
  checked,
  onSelect,
  title,
  description,
}: {
  testId: string
  checked: boolean
  onSelect: () => void
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        'cursor-pointer rounded-xl border px-3 py-2.5 text-left transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
        checked
          ? 'border-white/40 bg-white/[0.12]'
          : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]',
      )}
    >
      <span className="block text-sm text-white">{title}</span>
      <span className="mt-0.5 block text-xs font-medium leading-5 text-white/55">{description}</span>
    </button>
  )
}
